/// <reference types="vite/client" />

import React from 'react';
import * as Select from '@radix-ui/react-select';
import { ChevronDownIcon } from 'lucide-react';
// Custom styled Radix Select components for player dropdowns
const selectTriggerClasses =
  "w-full border rounded-md p-2 flex justify-between items-center bg-white text-left shadow-sm transition-colors focus:ring-2 focus:ring-[#30702F] focus:border-[#30702F] outline-none";
const selectContentClasses =
  "bg-white border border-gray-200 rounded-md shadow-lg animate-fadeIn overflow-hidden z-50";
const selectItemClasses =
  "px-3 py-2 cursor-pointer hover:bg-[#f1f5f9] data-[state=checked]:bg-[#30702F] data-[state=checked]:text-white data-[highlighted]:bg-[#e5e7eb] transition-colors flex items-center gap-2 text-sm";

// Animation for dropdown
const selectContentStyle = {
  animation: 'fadeIn 0.18s cubic-bezier(0.4, 0, 0.2, 1)'
};

// Keyframes for fadeIn
const styleSheet = document?.getElementById('radix-select-keyframes');
if (!styleSheet && typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.id = 'radix-select-keyframes';
  style.innerHTML = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-0.25rem);}
      to { opacity: 1; transform: none;}
    }
  `;
  document.head.appendChild(style);
}
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface Player {
  id: string;
  name: string;
  whs_id?: string;
  nominated?: boolean;
}

interface TeamWithPlayers {
  id: string;
  name: string;
  club_id: string;
  players: Player[];
}

interface FixtureResultsProps {
  fixture: {
    id: string;
    home_team: TeamWithPlayers;
    away_team: TeamWithPlayers;
    match_date: string;
    result: any;
    division_id: string;
    season_id: string;
    all_teams?: TeamWithPlayers[]; // This is injected by the parent, see FixturesPage.tsx or FixturesList.tsx
    allowEdit?: boolean; // Add this line
  };
  onClose: () => void;
}

const FixtureResults: React.FC<FixtureResultsProps> = ({ fixture, onClose }) => {
  const navigate = useNavigate();
  console.log("✅ Supabase instance:", supabase);
  const { home_team, away_team, match_date, result } = fixture;

  // Check if fixture is completed (results submitted) and not editable
  const isCompleted = fixture.allowEdit !== true;

  // Club logo state
  const [homeClubLogoPath, setHomeClubLogoPath] = React.useState('');
  const [awayClubLogoPath, setAwayClubLogoPath] = React.useState('');

  // Confirmation dialog state
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  React.useEffect(() => {
    const fetchClubLogos = async () => {
      const getClubLogoPath = async (clubId: string) => {
        const { data, error } = await supabase
          .from('clubs')
          .select('logo_path')
          .eq('id', clubId)
          .single();

        if (error) {
          console.error(`❌ Error fetching logo for club ID ${clubId}:`, error.message);
          return '';
        }

        return data?.logo_path ? encodeURIComponent(data.logo_path) : '';
      };

      const [homeLogo, awayLogo] = await Promise.all([
        getClubLogoPath(home_team.club_id),
        getClubLogoPath(away_team.club_id),
      ]);

      setHomeClubLogoPath(homeLogo);
      setAwayClubLogoPath(awayLogo);
    };

    fetchClubLogos();
  }, [home_team.club_id, away_team.club_id]);

  // Store submitter info for stable rendering
  const [submitInfo, setSubmitInfo] = React.useState<{ id: string, at: string } | null>(null);

  // Reset local state when fixture changes
  React.useEffect(() => {
    setMatchResults(
      Array.from({ length: 5 }, () => ({
        homePlayerId: '',
        awayPlayerId: '',
        result: ''
      }))
    );
    setOpenMatchIndex(0);
  }, [fixture.id]);

  // Cleanup effect to clear modal state on unmount
  React.useEffect(() => {
    return () => {
      // Clear internal states on unmount
      setMatchResults(
        Array.from({ length: 5 }, () => ({
          homePlayerId: '',
          awayPlayerId: '',
          result: ''
        }))
      );
      setOpenMatchIndex(0);
    };
  }, []);

  // Load result and matchups from Supabase if result is missing
  React.useEffect(() => {
    const fetchFullResult = async () => {
      if (!fixture.result) {
        const { data, error } = await supabase
          .from('results')
          .select(`
            *,
            matchups (
              id,
              result,
              home_player:home_player_id (
                id,
                name,
                nominated
              ),
              away_player:away_player_id (
                id,
                name,
                nominated
              )
            )
          `)
          .eq('fixture_id', fixture.id)
          .maybeSingle();
        console.log("🧪 Fetched result:", data);
        if (error) console.error("❌ Result fetch error:", error);
        if (!data) console.warn("⚠️ No result found for fixture", fixture.id);

        if (error) {
          console.error('❌ Error fetching fixture result with matchups:', error.message);
          return;
        }

        if (data) {
          console.log("📥 fetched result with matchups:", data);
          setMatchResults(
            data.matchups.map((m: any) => ({
              homePlayerId: m.home_player.id,
              awayPlayerId: m.away_player.id,
              result: m.result,
              homePlayer: m.home_player,
              awayPlayer: m.away_player,
            }))
          );
          console.log("✅ Loaded matchResults:", data.matchups.map((m: any) => ({
            home: m.home_player?.name,
            away: m.away_player?.name,
            result: m.result
          })));
          // Do NOT overwrite submittedBy here; rely on current submission flow's user ID for new writes
          fixture.result = {
            matches: data.matchups.map((m: any) => ({
              homePlayer: m.home_player,
              awayPlayer: m.away_player,
              result: m.result,
            })),
            homeScore: data.home_score,
            awayScore: data.away_score,
            // submittedBy: data.submitted_by, // <-- REMOVE this line entirely
            submittedAt: data.submitted_at
          };
          setSubmitInfo({
            id: data.submitted_by,
            at: data.submitted_at
          });
        }
      }
    };

    fetchFullResult();
  }, [fixture]);

  const allHomeClubPlayers = home_team.players || [];
  const allAwayClubPlayers = away_team.players || [];

  // Use all_teams from fixture if available
  const allTeams: TeamWithPlayers[] = fixture.all_teams || [];
  console.log("📭 all_teams received in FixtureResults:", allTeams);

  function parseRoman(str: string): number {
    const romanMap: Record<string, number> = {
      I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6
    };
    const parts = str.trim().split(' ');
    return romanMap[parts[parts.length - 1]] || 99;
  }

  const homeTeamNumber = parseRoman(home_team.name);
  const awayTeamNumber = parseRoman(away_team.name);

  console.log("📦 allTeams (raw):", allTeams);

  // --- New logic to fetch all players for a club given a team_id ---
  // These states will hold all players for the home and away clubs
  const [extendedHomePlayers, setExtendedHomePlayers] = React.useState<{ id: string; name: string; nominated?: boolean; team_name: string }[]>([]);
  const [extendedAwayPlayers, setExtendedAwayPlayers] = React.useState<{ id: string; name: string; nominated?: boolean; team_name: string }[]>([]);

  React.useEffect(() => {
    // Helper to fetch all players for a club given a team_id
    const fetchClubPlayers = async (teamId: string, setPlayers: (players: any[]) => void) => {
      // 1. Fetch the club_id of the team
      const { data: teamData, error: teamError } = await supabase
        .from('teams')
        .select('club_id')
        .eq('id', teamId)
        .single();
      if (teamError || !teamData?.club_id) {
        setPlayers([]);
        return;
      }
      const clubId = teamData.club_id;
      // 2. Fetch all teams in this club
      const { data: teamsInClub, error: teamsError } = await supabase
        .from('teams')
        .select('id,name')
        .eq('club_id', clubId);
      if (teamsError || !teamsInClub) {
        setPlayers([]);
        return;
      }
      const teamIds = teamsInClub.map((t: any) => t.id);
      // 3. Fetch all players whose team_id is in teamIds
      const { data: players, error: playersError } = await supabase
        .from('players')
        .select('*')
        .in('team_id', teamIds);
      if (playersError || !players) {
        setPlayers([]);
        return;
      }
      // 4. Attach team_name to each player
      const teamNameMap: Record<string, string> = {};
      teamsInClub.forEach((t: any) => {
        teamNameMap[t.id] = t.name;
      });
      const playersWithTeamName = players.map((p: any) => ({
        ...p,
        team_name: teamNameMap[p.team_id] || '',
      }));
      // 5. Sort by team_name (A-Z), then by last name (A-Z)
      playersWithTeamName.sort((a: any, b: any) => {
        // First, sort by team_name
        const teamCompare = (a.team_name || '').localeCompare(b.team_name || '');
        if (teamCompare !== 0) return teamCompare;
        // Then, by last name (second part of name)
        const aLast = (a.name || '').split(' ')[1] || '';
        const bLast = (b.name || '').split(' ')[1] || '';
        return aLast.localeCompare(bLast);
      });
      setPlayers(playersWithTeamName);
    };

    // Fetch for home and away teams
    if (home_team?.id) {
      fetchClubPlayers(home_team.id, setExtendedHomePlayers);
    }
    if (away_team?.id) {
      fetchClubPlayers(away_team.id, setExtendedAwayPlayers);
    }
    // We want to refetch if the team changes
  }, [home_team?.id, away_team?.id]);

  const [matchResults, setMatchResults] = React.useState<any[]>(
    Array.from({ length: 5 }, () => ({
      homePlayerId: '',
      awayPlayerId: '',
      result: ''
    }))
  );
  const [openMatchIndex, setOpenMatchIndex] = React.useState<number | null>(0);
  const lastMatchResultsRef = React.useRef(matchResults);

  const totalPoints = matchResults.reduce((sum, match) => {
    if (match.result === 'win') return sum + 1;
    if (match.result === 'half') return sum + 0.5;
    if (match.result === 'loss') return sum + 1;
    return sum;
  }, 0);

  const homePoints = matchResults.reduce((sum, match) => {
    if (match.result === 'win') return sum + 1;
    if (match.result === 'half') return sum + 0.5;
    return sum;
  }, 0);

  const awayPoints = matchResults.reduce((sum, match) => {
    if (match.result === 'loss') return sum + 1;
    if (match.result === 'half') return sum + 0.5;
    return sum;
  }, 0);

  const handleSubmitResults = async () => {
    setShowConfirmation(true);
  };

  // Confirm submission logic
  const confirmSubmission = async () => {
    setShowConfirmation(false);
    if (!allTeams || allTeams.length === 0) {
      console.error('❌ allTeams is empty before submission');
      alert('Cannot submit results – team data is missing.');
      return;
    }
    try {
      // Get the current user ID for submitted_by
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('❌ Error getting user:', userError);
        throw userError;
      }
      const userId = userData?.user?.id || null;
      if (!userId) {
        alert("Could not determine submitting user. Please log in.");
        return;
      }

      // Bonus calculation
      let bonus_home = 0;
      let bonus_away = 0;
      if (homePoints > 2.5) {
        bonus_home = 2;
      } else if (awayPoints > 2.5) {
        bonus_away = 2;
      } else if (homePoints === 2.5 && awayPoints === 2.5) {
        bonus_home = 1;
        bonus_away = 1;
      }

      // Upsert result with submitted_by, home_team_id, and away_team_id, allowing overwrite if already exists
      const { data: resultInsert, error: resultError } = await supabase
        .from('results')
        .upsert([
          {
            fixture_id: fixture.id,
            home_team_id: home_team.id,
            away_team_id: away_team.id,
            home_score: homePoints,
            away_score: awayPoints,
            submitted_by: userId, // Always use current authenticated user
            submitted_at: new Date().toISOString(),
            bonus_home,
            bonus_away
          }
        ], {
          onConflict: 'fixture_id',
          ignoreDuplicates: false,
        })
        .select()
        .single();

      if (resultError) {
        console.error("❌ Result insert error:", resultError);
        throw resultError;
      }

      const resultId = resultInsert.id;

      const matchupInserts = matchResults.map((match, index) => {
        let outcome: 'win' | 'loss' | 'half' = 'half';
        if (match.result === 'win') outcome = 'win';
        else if (match.result === 'loss') outcome = 'loss';

        return {
          fixture_id: fixture.id,
          result_id: resultId,
          home_player_id: match.homePlayerId,
          away_player_id: match.awayPlayerId,
          result: match.result,
          index,
          game_number: index + 1,
          outcome
        };
      });

      const { error: matchupsError } = await supabase
        .from('matchups')
        .insert(matchupInserts);

      if (matchupsError) {
        console.error("❌ Matchups insert error:", matchupsError);
        throw matchupsError;
      }

      // Determine win/loss/draw for standings update
      let homeOutcome = '';
      let awayOutcome = '';
      let homePointsForStandings = 0;
      let awayPointsForStandings = 0;

      if (homePoints > awayPoints) {
        homeOutcome = 'win';
        awayOutcome = 'loss';
        homePointsForStandings = 2;
        awayPointsForStandings = 0;
      } else if (homePoints < awayPoints) {
        homeOutcome = 'loss';
        awayOutcome = 'win';
        homePointsForStandings = 0;
        awayPointsForStandings = 2;
      } else {
        homeOutcome = 'half';
        awayOutcome = 'half';
        homePointsForStandings = 1;
        awayPointsForStandings = 1;
      }

      // You may already have the division_id in props or need to pass it in
      const divisionId = fixture.division_id; // ensure this is passed in from parent

      // Upsert standings for both teams
      const { error: standingsError } = await supabase
        .from('standings')
        .upsert([
          {
            team_id: home_team.id,
            division_id: divisionId,
            season_id: fixture.season_id,
            matches_played: 1,
            matches_won: homeOutcome === 'win' ? 1 : 0,
            matches_half: homeOutcome === 'half' ? 1 : 0,
            matches_lost: homeOutcome === 'loss' ? 1 : 0,
            total_points: homePointsForStandings
          },
          {
            team_id: away_team.id,
            division_id: divisionId,
            season_id: fixture.season_id,
            matches_played: 1,
            matches_won: awayOutcome === 'win' ? 1 : 0,
            matches_half: awayOutcome === 'half' ? 1 : 0,
            matches_lost: awayOutcome === 'loss' ? 1 : 0,
            total_points: awayPointsForStandings
          }
        ], { onConflict: 'team_id,division_id' });

      if (standingsError) {
        console.error("❌ Standings upsert error:", standingsError);
        throw standingsError;
      }

      // Update fixture status to 'completed'
      await supabase
        .from('fixtures')
        .update({ status: 'completed' })
        .eq('id', fixture.id);

      navigate(`/projects/gwent/standings?division=${fixture.division_id}`);
    } catch (error) {
      console.error('🔥 Error submitting results:', error);
      alert(`There was an error submitting the results: ${error.message || error}`);
    }
  };


  return (
    <Dialog open onOpenChange={onClose} data-lov-id="fixture-results-root">
      <DialogContent className="max-w-xl" aria-describedby="fixture-results-description">
        <p id="fixture-results-description" className="sr-only">
          Enter or view fixture match results between the two teams.
        </p>
        <DialogHeader className="pb-0 mb-0">
          <DialogTitle className="sr-only">Fixture Results</DialogTitle>
          {match_date && (
            <div className="text-center text-sm text-muted-foreground mb-1">
              {format(new Date(match_date + "T00:00:00Z"), "eee, dd MMM yyyy")}
            </div>
          )}
          <div className="flex flex-col items-center gap-1 text-center">
            <div className="flex justify-between w-full items-start gap-12 text-lg font-semibold">
              <div className="flex flex-col items-center flex-1">
                {homeClubLogoPath && (
                  <img
                    src={`https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/${homeClubLogoPath}`}
                    alt="Home Club Logo"
                    className="h-10 mb-1"
                  />
                )}
                <span>{home_team.name}</span>
                <span className="text-[#30702F] font-bold text-2xl mt-1">{homePoints}</span>
              </div>
              <div className="text-muted-foreground text-base self-center">vs</div>
              <div className="flex flex-col items-center flex-1">
                {awayClubLogoPath && (
                  <img
                    src={`https://kektmescyfgvwazyljph.supabase.co/storage/v1/object/public/club-logos/${awayClubLogoPath}`}
                    alt="Away Club Logo"
                    className="h-10 mb-1"
                  />
                )}
                <span>{away_team.name}</span>
                <span className="text-[#30702F] font-bold text-2xl mt-1">{awayPoints}</span>
              </div>
            </div>
          </div>
        </DialogHeader>

        {isCompleted ? (
          <div data-lov-id="readonly-match-results">
            {result && Array.isArray(result.matches) && result.matches.length > 0 && (
              <div className="pt-4 space-y-3">
                {result.matches.map((match, index) => {
                  const isHomeWinner = match.result === 'win';
                  const isAwayWinner = match.result === 'loss';
                  const isHalved = match.result === 'half';

                  if (isHalved) {
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between border rounded-md overflow-hidden"
                      >
                        <div className="w-1/2 p-3">
                          <div className="text-sm font-medium flex items-center justify-between">
                            <span>{match.homePlayer.name}</span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {match.homePlayer.nominated ? 'Nominated' : 'Not Nominated'}
                          </div>
                        </div>

                        <div className="w-2/5 text-center bg-gray-300 text-gray-900 text-sm font-semibold py-4 relative">
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full w-0 h-0 border-y-12 border-r-12 border-y-transparent border-r-gray-300"></div>
                          <span>A/S</span>
                          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full w-0 h-0 border-y-12 border-l-12 border-y-transparent border-l-gray-300"></div>
                        </div>

                        <div className="w-1/2 text-right p-3">
                          <div className="text-sm font-medium flex justify-between items-center w-full">
                            <span className="ml-auto">{match.awayPlayer.name}</span>
                          </div>
                          <div className="text-xs text-gray-400">
                            {match.awayPlayer.nominated ? 'Nominated' : 'Not Nominated'}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between border rounded-md overflow-hidden"
                    >
                      <div className={`w-1/2 p-3 ${isHomeWinner ? 'bg-[#30702F] text-white' : ''}`}>
                        <div className="text-sm font-medium flex items-center justify-between">
                          <span>{match.homePlayer.name}</span>
                        </div>
                        <div className="text-xs text-gray-400">
                          {match.homePlayer.nominated ? 'Nominated' : 'Not Nominated'}
                        </div>
                      </div>

                      <div className="flex-1 text-center text-s italic text-[#30702F]">
                        {/* Only show Halved for halved matches, which are handled above */}
                      </div>

                      <div className={`w-1/2 text-right p-3 ${isAwayWinner ? 'bg-[#30702F] text-white' : ''}`}>
                        <div className="text-sm font-medium flex justify-between items-center w-full">
                          <span className="ml-auto">{match.awayPlayer.name}</span>
                        </div>
                        <div className="text-xs text-gray-400">
                          {match.awayPlayer.nominated ? 'Nominated' : 'Not Nominated'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Display submitter info if available */}
            {submitInfo && (
              <SubmittedByInfo
                submittedBy={submitInfo.id}
                submittedAt={submitInfo.at}
              />
            )}
          </div>
        ) : (
          matchResults.length > 0 ? (
            (() => {
              // Handler to update a match: only auto-collapse all on final match completion.
              const handleMatchUpdate = (index: number, updatedMatch: any) => {
                const updated = [...matchResults];
                updated[index] = { ...updated[index], ...updatedMatch };
                setMatchResults(updated);

                // Only one accordion can be open at a time — handled by openMatchIndex
                // When match 5 (index 4) is completed, collapse all
                if (
                  index === 4 &&
                  updated[4].homePlayerId && updated[4].awayPlayerId && updated[4].result
                ) {
                  setTimeout(() => setOpenMatchIndex(null), 400);
                }

                // Do not auto-collapse or open any other accordion for earlier matches
                // The completed match remains open until user manually switches
              };
              return (
                <div data-lov-id="editable-match-results">
                  <div className="space-y-2">
                    {[...Array(5)].map((_, index) => {
                      // Summary string logic
                      const match = matchResults[index];
                      const homePlayer = extendedHomePlayers.find(p => p.id === match.homePlayerId);
                      const awayPlayer = extendedAwayPlayers.find(p => p.id === match.awayPlayerId);
                      let summary = '';
                      if (match.result && homePlayer && awayPlayer) {
                        if (match.result === 'half') summary = 'Halved';
                        else if (match.result === 'win') summary = `${homePlayer.team_name} won`;
                        else if (match.result === 'loss') summary = `${awayPlayer.team_name} won`;
                      }
                      return (
                        <div
                          key={index}
                          className="border rounded-md mb-2 overflow-hidden"
                        >
                          <button
                            onClick={() => setOpenMatchIndex(openMatchIndex === index ? null : index)}
                            className={cn(
                              "w-full text-left px-4 py-2 font-medium flex justify-between items-center",
                              match.homePlayerId && match.awayPlayerId && match.result
                                ? "bg-[#30702F] text-white"
                                : "bg-gray-100 text-black"
                            )}
                            type="button"
                          >
                            <span className="text-sm text-left w-full truncate">
                              <span className="font-medium mr-2">Match {index + 1}:</span>
                              {openMatchIndex === index ? null : summary && (
                                <span
                                  className={cn(
                                    "text-sm italic",
                                    match.homePlayerId && match.awayPlayerId && match.result
                                      ? "text-white"
                                      : "text-muted-foreground"
                                  )}
                                >
                                  {summary}
                                </span>
                              )}
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 ml-2 transition-transform ${openMatchIndex === index ? 'rotate-180' : 'rotate-0'}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {openMatchIndex === index ? (
                            <>
                              <div className="p-3">
                                <div className="flex items-center justify-between gap-4">
                                  {/* Home Player Selection */}
                                  <div className="flex-1">
                                    <Select.Root
                                      value={matchResults[index].homePlayerId}
                                      onValueChange={value => handleMatchUpdate(index, { homePlayerId: value })}
                                      disabled={isCompleted}
                                    >
                                      <Select.Trigger className="inline-flex items-center justify-between rounded px-3 py-2 text-sm border border-gray-300 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full" aria-label="Select Home Player">
                                        <Select.Value
                                          placeholder="Select player"
                                          className="text-left"
                                          children={extendedHomePlayers.find(p => p.id === matchResults[index].homePlayerId)?.name || undefined}
                                        />
                                        <Select.Icon className="ml-2">
                                          <ChevronDownIcon />
                                        </Select.Icon>
                                      </Select.Trigger>
                                      <Select.Content
                                        side="bottom"
                                        sideOffset={4}
                                        position="popper"
                                        align="center"
                                        className="bg-white border border-gray-300 rounded shadow-lg z-[1000] max-h-64 overflow-auto w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
                                      >
                                        <Select.Viewport>
                                          {Object.entries(
                                            extendedHomePlayers.reduce((acc, player) => {
                                              if (!acc[player.team_name]) acc[player.team_name] = [];
                                              acc[player.team_name].push(player);
                                              return acc;
                                            }, {} as Record<string, typeof extendedHomePlayers>)
                                          ).map(([teamName, players]) => (
                                            <React.Fragment key={teamName}>
                                              <div style={{ marginTop: '4px' }} />
                                              <div
                                                style={{
                                                  backgroundColor: '#f2f2f2',
                                                  color: '#333',
                                                  padding: '4px 8px',
                                                  fontWeight: 'bold',
                                                  fontSize: '14px',
                                                  marginTop: '8px',
                                                  borderRadius: '4px',
                                                }}
                                              >
                                                {teamName}
                                              </div>
                                              <div style={{ marginBottom: '4px' }} />
                                              {players.map((player) => {
                                                const isSelected = matchResults.some((m, i) => i !== index && m.homePlayerId === player.id);
                                                return (
                                                  <Select.Item
                                                    key={player.id}
                                                    value={player.id}
                                                    disabled={isSelected}
                                                    className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer${isSelected ? " opacity-40" : ""}`}
                                                  >
                                                    <div style={{ textAlign: 'left', width: '100%' }}>{player.name}</div>
                                                    {isSelected && (
                                                      <span className="text-xs text-gray-400 ml-2">(already selected)</span>
                                                    )}
                                                  </Select.Item>
                                                );
                                              })}
                                            </React.Fragment>
                                          ))}
                                        </Select.Viewport>
                                      </Select.Content>
                                    </Select.Root>
                                    <div className="flex gap-1 mt-2">
                                      {['win', 'half', 'loss'].map(type => (
                                        <button
                                          key={type}
                                          type="button"
                                          className={`px-2 py-1 border rounded text-xs ${
                                            matchResults[index].result === type
                                              ? 'bg-gray-200 font-bold'
                                              : 'hover:bg-gray-100'
                                          }`}
                                          onClick={() => {
                                            handleMatchUpdate(index, { result: type });
                                          }}
                                          disabled={isCompleted}
                                          style={isCompleted ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                        >
                                          {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  {/* Away Player Selection */}
                                  <div className="flex-1" style={{ textAlign: 'left', justifyContent: 'flex-start' }}>
                                    <Select.Root
                                      value={matchResults[index].awayPlayerId}
                                      onValueChange={value => handleMatchUpdate(index, { awayPlayerId: value })}
                                      disabled={isCompleted}
                                    >
                                      <Select.Trigger className="inline-flex items-center justify-between rounded px-3 py-2 text-sm border border-gray-300 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full" aria-label="Select Away Player">
                                        <Select.Value
                                          placeholder="Select player"
                                          className="text-left"
                                          children={extendedAwayPlayers.find(p => p.id === matchResults[index].awayPlayerId)?.name || undefined}
                                        />
                                        <Select.Icon className="ml-2">
                                          <ChevronDownIcon />
                                        </Select.Icon>
                                      </Select.Trigger>
                                      <Select.Content
                                        side="bottom"
                                        sideOffset={4}
                                        position="popper"
                                        align="center"
                                        className="bg-white border border-gray-300 rounded shadow-lg z-[1000] max-h-64 overflow-auto w-full scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200"
                                      >
                                        <Select.Viewport>
                                          {Object.entries(
                                            extendedAwayPlayers.reduce((acc, player) => {
                                              if (!acc[player.team_name]) acc[player.team_name] = [];
                                              acc[player.team_name].push(player);
                                              return acc;
                                            }, {} as Record<string, typeof extendedAwayPlayers>)
                                          ).map(([teamName, players]) => (
                                            <React.Fragment key={teamName}>
                                              <div style={{ marginTop: '4px' }} />
                                              <div
                                                style={{
                                                  backgroundColor: '#f2f2f2',
                                                  color: '#333',
                                                  padding: '4px 8px',
                                                  fontWeight: 'bold',
                                                  fontSize: '14px',
                                                  marginTop: '8px',
                                                  borderRadius: '4px',
                                                }}
                                              >
                                                {teamName}
                                              </div>
                                              <div style={{ marginBottom: '4px' }} />
                                              {players.map((player) => {
                                                const isSelected = matchResults.some((m, i) => i !== index && m.awayPlayerId === player.id);
                                                return (
                                                  <Select.Item
                                                    key={player.id}
                                                    value={player.id}
                                                    disabled={isSelected}
                                                    className={`px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer${isSelected ? " opacity-40" : ""}`}
                                                  >
                                                    <div style={{ textAlign: 'left', width: '100%' }}>{player.name}</div>
                                                    {isSelected && (
                                                      <span className="text-xs text-gray-400 ml-2">(already selected)</span>
                                                    )}
                                                  </Select.Item>
                                                );
                                              })}
                                            </React.Fragment>
                                          ))}
                                        </Select.Viewport>
                                      </Select.Content>
                                    </Select.Root>
                                    <div className="flex gap-1 mt-2 justify-end">
                                      {['win', 'half', 'loss'].map(type => (
                                        <button
                                          key={type}
                                          type="button"
                                          className={`px-2 py-1 border rounded text-xs ${
                                            (matchResults[index].result === 'win' && type === 'loss') ||
                                            (matchResults[index].result === 'loss' && type === 'win') ||
                                            (matchResults[index].result === 'half' && type === 'half')
                                              ? 'bg-gray-200 font-bold'
                                              : 'opacity-50 cursor-not-allowed'
                                          }`}
                                          disabled
                                        >
                                          {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {match.homePlayerId && match.awayPlayerId && match.result && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {/* <span className="font-medium mr-2">Match {index + 1}:</span>
                                  <span className="text-muted-foreground text-sm italic">{summary}</span> */}
                                </div>
                              )}
                              {/* Final match summary below accordion only after match 5 is completed */}
                              {index === 4 && match.homePlayerId && match.awayPlayerId && match.result && (
                                <div className="mt-2 text-xs text-muted-foreground text-center">
                                  {/* <span className="font-medium mr-2">Match 5:</span>
                                  <span className="text-muted-foreground text-sm italic">{summary}</span> */}
                                </div>
                              )}
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-6 text-right">
                    <Button
                      variant="default"
                      onClick={handleSubmitResults}
                      disabled={isCompleted || matchResults.some(m => !m.homePlayerId || !m.awayPlayerId || !m.result)}
                      className="bg-[#30702F] hover:bg-[#2a6028] text-white"
                    >
                      Submit Results
                    </Button>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="text-sm text-muted-foreground text-center py-4">
              Match results not loaded.
            </div>
          )
        )}
        {/* Keep matchResults in ref for possible future use */}
        {/*
        React.useEffect(() => {
          lastMatchResultsRef.current = matchResults;
        }, [matchResults]);
        */}
        {/*
          The above effect is not needed here, instead, include it below at component level.
        */}
      </DialogContent>
      {/* Confirmation Dialog */}
      {showConfirmation && (
        <Dialog open onOpenChange={() => setShowConfirmation(false)}>
          <DialogContent className="max-w-sm text-center space-y-4">
            <DialogHeader>
              <DialogTitle className="text-base font-medium">
                Submit results?
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Once submitted, leagues will be auto-updated and results can only be changed by a GGU admin.
            </p>
            <DialogFooter className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowConfirmation(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmSubmission}
                className="bg-[#30702F] hover:bg-[#2a6028] text-white"
              >
                Yes, Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};


export default FixtureResults;
// Helper component to display submitter info with submitter's name
const SubmittedByInfo: React.FC<{ submittedBy: string; submittedAt: string }> = ({
  submittedBy,
  submittedAt,
}) => {
  const [submitterName, setSubmitterName] = React.useState<string>('');

  React.useEffect(() => {
    const fetchSubmitterName = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('name')
        .eq('id', submittedBy)
        .single();

      if (error) {
        console.error('Error fetching submitter name:', error);
        return;
      }

      setSubmitterName(data?.name || '');
    };

    if (submittedBy) {
      fetchSubmitterName();
    }
  }, [submittedBy]);

  const date = new Date((submittedAt || '') + 'Z');
  const formattedDate = date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });

  return (
    <div className="text-xs text-muted-foreground text-right mt-2">
      Submitted {formattedDate}{submitterName ? ` by ${submitterName}` : ''}
    </div>
  );
}
// Import date-fns format if not already imported
import { format } from "date-fns";